import { useEffect, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import axios from 'axios';
import {
  useApolloClient,
  useLazyQuery,
  useQuery,
  useMutation,
} from '@apollo/client/react';

import { logger } from '@shared/utils';

import { getDomain, getUser } from './queries';
import { getLocale, getDomainName, environment } from '../../../config';
import { capture, identify, reset } from '../../../analytics/posthog';

export default () => {
  const [databaseUser, setDatabaseUser] = useState();
  const [backofficeAccess, setBackofficeAccess] = useState(true);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [user, setUser] = useState();
  const [videocationCatalog, setVideocationCatalog] = useState();

  const domain = getDomainName();
  const locale = getLocale();
  const client = useApolloClient();

  const headers = {
    locale,
  };

  if (import.meta.env.MODE === 'development') {
    Object.assign(headers, {
      domain,
    });
  }

  const getRestClient = () => {
    const axiosInstance = axios.create({
      baseURL:
        domain === 'es'
          ? import.meta.env.VITE_REST_API_ES
          : import.meta.env.VITE_REST_API_NO,
      headers,
    });

    axiosInstance.interceptors.request.use(async (config) => {
      let token;
      try {
        const session = await Auth.currentSession();
        token = session.getAccessToken().getJwtToken();
      } catch (error) {
        logger.error(error);
      }

      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: token ? `Bearer ${token}` : '',
        },
      };
    });

    return axiosInstance;
  };

  const useNewGraphQLQuery = (query, options, queryHeaders) =>
    useQuery(query, {
      ...options,
      context: {
        headers: { ...headers, ...queryHeaders },
      },
    });

  const useLazyGraphQLQuery = (query, options, queryHeaders) =>
    useLazyQuery(query, {
      ...options,
      context: {
        headers: { ...headers, ...queryHeaders },
      },
    });

  const useGraphQLMutation = (mutation, options, mutationHeaders) =>
    useMutation(mutation, {
      ...options,
      context: {
        headers: { ...headers, ...mutationHeaders },
      },
    });

  const [getUserQuery, getUserResponse] = useLazyGraphQLQuery(getUser, {
    fetchPolicy: 'network-only',
  });
  const [getDomainQuery, getDomainResponse] = useLazyGraphQLQuery(getDomain);

  const refreshUserData = ({ attributes } = {}) => {
    if (attributes?.sub || user?.id) {
      getUserQuery({
        variables: {
          userSub: attributes?.sub || user?.id,
        },
      });
    } else {
      setLoadingUser(false);
    }
  };

  const fetchUser = async () => {
    const userInfo = await Auth.currentUserInfo();
    if (userInfo) {
      refreshUserData(userInfo);
      const { attributes, signInUserSession } =
        await Auth.currentAuthenticatedUser();
      const { name, email } = attributes;

      identify(attributes.sub, { email, environment });

      setUser({
        attributes: { attributes, ...userInfo.attributes },
        email,
        name,
        signInUserSession,
      });
    } else {
      setUser();
      setLoadingUser(false);
    }
  };

  const fetchDomain = () => {
    getDomainQuery({
      variables: {
        domain,
      },
    });
  };

  const fetchSettings = () => {
    setTimeout(() => setLoadingSettings(false), 500);
  };

  useEffect(() => {
    const { data, loading, error } = getUserResponse;
    if (!loading && data?.getUser) {
      const { getUser } = data;
      setDatabaseUser({
        id: getUser.id,
        email: getUser.email,
        name: getUser.name,
      });
      setLoadingUser(false);
    } else if (!loading && error) {
      logger.error('Error fetching user data');
      setLoadingUser(false);
      setBackofficeAccess(false);
    }
  }, [getUserResponse]);

  useEffect(() => {
    const { loading, data } = getDomainResponse;
    if (!loading && data?.getDomain) {
      const { videocationCatalog: vcCatalog } = data.getDomain;
      setVideocationCatalog(vcCatalog);
      if (loadingSettings) {
        setLoadingSettings(false);
      }
    }
  }, [getDomainResponse]);

  useEffect(() => {
    fetchUser();
    fetchSettings();
    fetchDomain();

    Hub.listen('auth', async ({ payload: { event } }) => {
      if (event === 'signIn') {
        capture('admin_signed_in');
        fetchUser();
      } else if (event === 'signOut') {
        reset();
        setBackofficeAccess(true);
        setUser();
        client.clearStore();
      }
    });
  }, []);

  return {
    backofficeAccess,
    databaseUser,
    domain,
    getRestClient,
    loadingSettings,
    loadingUser,
    locale,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    useNewGraphQLQuery,
    user,
    videocationCatalog,
  };
};
